import Konva from 'konva';
import React, { memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Image, Layer, Rect, Stage, Transformer } from 'react-konva';
import { InputNumber } from 'tdesign-react';
import useImage from 'use-image';

import { ImageRatio } from '@/const/richMedia';
import { PRIMARY_COLOR } from '@/const/style';

import CustomImage from './customImage';
import styles from './index.module.less';
import { getAbsolutePosition, getPositionPercentage } from './util';

export interface KonvaImage {
  id: string;
  url: string;
  x?: number;
  y?: number;
  scale?: number;
}

export interface IKonvaRef {
  getImages: () => KonvaImage[];
}

interface IProps {
  konvaRef: React.ForwardedRef<IKonvaRef>;
  value: KonvaImage[];
  disabled?: boolean;
  background?: string;
}

const DEFAULT_RATIO = ImageRatio['812:375'];
export const INIT_COORDINATE = 0.00001;
export const INIT_SCALE = 0.8;
export const MIN_SCALE = 0.1;
export const MAX_SCALE = 3;

const CustomKonva: React.FC<IProps> = memo((props) => {
  const { konvaRef, value = [], disabled = false, background } = props;
  const [bgImage, status] = useImage(background || '', 'anonymous');
  const [bgSize, setBgSize] = useState(DEFAULT_RATIO);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [posX, setPosX] = useState(0);
  const [posY, setPosY] = useState(0);
  const [scale, setScale] = useState(INIT_SCALE);

  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const imageRefs = useRef<Map<string, Konva.Image>>(new Map());
  const coordinateCache = useRef<Record<string, { x: number; y: number; scale: number }>>({});

  useImperativeHandle(konvaRef, () => ({
    getImages: () => getImageData(),
  }));

  useEffect(() => {
    if (status === 'loaded') {
      onBgLoad();
    }
  }, [status]);

  // 背景图片加载，计算背景图尺寸
  const onBgLoad = useCallback(() => {
    const naturalWidth = bgImage?.naturalWidth || DEFAULT_RATIO.width;
    const naturalHeight = bgImage?.naturalHeight || DEFAULT_RATIO.height;
    const aspectRatio = naturalWidth / naturalHeight;

    let targetWidth;
    let targetHeight;
    if (aspectRatio > 1) {
      targetWidth = DEFAULT_RATIO.width;
      targetHeight = Math.round(targetWidth / aspectRatio);
    } else {
      targetHeight = DEFAULT_RATIO.height;
      targetWidth = Math.round(targetHeight * aspectRatio);
    }

    setBgSize({
      width: targetWidth,
      height: targetHeight,
    });
  }, [bgImage]);

  const imageList = useMemo(() => {
    const newCache = { ...coordinateCache.current };
    const currentIds = new Set(value.map(img => img.id));

    // 添加新图片到缓存
    value.forEach((img) => {
      if (!newCache[img.id]) {
        newCache[img.id] = {
          x: img.x ?? INIT_COORDINATE,
          y: img.y ?? INIT_COORDINATE,
          scale: img.scale || INIT_SCALE,
        };
      }
    });

    // 删除已移除图片的缓存
    Object.keys(newCache).forEach((id) => {
      if (!currentIds.has(id)) {
        delete newCache[id];
      }
    });

    coordinateCache.current = newCache;

    return value?.map((img) => {
      const cache = newCache[img.id] || {};
      return {
        ...img,
        key: img.id, // 唯一key用于避免重渲染
        x: cache.x ?? INIT_COORDINATE,
        y: cache.y ?? INIT_COORDINATE,
        scale: cache.scale || INIT_SCALE,
      };
    });
  }, [value]);

  // 监听选中图片变化，更新输入框
  useEffect(() => {
    if (selectedId) {
      const cache = coordinateCache.current[selectedId];
      if (!cache) return;

      const { x, y, scale } = cache;
      setPosX(x);
      setPosY(y);
      setScale(scale);
    } else {
      // 没有选中时清空输入框
      setPosX(0);
      setPosY(0);
      setScale(INIT_SCALE);
    }
  }, [selectedId]);

  // 获取所有图片的坐标和缩放数据
  const getImageData = (): KonvaImage[] => {
    const mapValue = new Map(value?.map(v => [v.id, v]));
    return Array.from(imageRefs.current.values()).map((node) => {
      const id = node.id();
      const cache = coordinateCache.current[id];
      const { x, y, scale } = cache;
      const url = mapValue.get(id)?.url || '';
      return { id, url, x, y, scale };
    });
  };

  // 处理输入框变化
  const handlePositionChange = useCallback((type: 'x' | 'y' | 'scale', value: number) => {
    if (!selectedId) return;
    const node = imageRefs.current.get(selectedId);
    if (!node) return;

    let absolutePos = { x: 0, y: 0 };
    if (type === 'scale') {
      // 限制缩放范围
      const newScale = Math.min(Math.max(value, MIN_SCALE), MAX_SCALE);
      // 计算绝对坐标
      const cache = coordinateCache.current[selectedId];
      absolutePos = getAbsolutePosition(
        bgSize,
        cache.x,
        cache.y,
        node.width(),
        node.height(),
        newScale,
      );

      // 更新状态
      node.x(absolutePos.x);
      node.y(absolutePos.y);
      node.scaleX(newScale);
      node.scaleY(newScale);
      setScale(newScale);

      // 更新缓存中的值
      coordinateCache.current[selectedId] = {
        ...coordinateCache.current[selectedId],
        [type]: newScale,
      };
    } else {
      const cache = coordinateCache.current[selectedId];
      const newPercentage = { ...cache, [type]: value };
      absolutePos = getAbsolutePosition(
        bgSize,
        newPercentage.x,
        newPercentage.y,
        node.width(),
        node.height(),
        node.scaleX(),
      );

      node.x(absolutePos.x);
      node.y(absolutePos.y);

      if (type === 'x') setPosX(value);
      if (type === 'y') setPosY(value);
      // 更新缓存中的值
      coordinateCache.current[selectedId] = {
        ...coordinateCache.current[selectedId],
        [type]: value,
      };
    }

    node.getLayer()?.batchDraw();
  }, [selectedId, bgSize]);

  // 监听图片变换事件
  const handleImageTransform = useCallback((id: string) => {
    const node = imageRefs.current.get(id);
    if (!node) return;

    // 更新缓存
    const percentage = getPositionPercentage(bgSize, node);
    const { x, y } = percentage;
    coordinateCache.current[id] = {
      ...coordinateCache.current[id],
      x,
      y,
      scale: node.scaleX(),
    };

    // 更新输入框
    if (selectedId === id) {
      setPosX(x);
      setPosY(y);
      setScale(node.scaleX());
    }
  }, [selectedId, bgSize]);

  // 点击图片时设置为选中
  const handleImageClick = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  // 点击空白区域取消选中
  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      transformerRef.current?.nodes([]);
      setSelectedId(null);
      return;
    }
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.header}
        style={{ flexDirection: bgSize.height > bgSize.width ? 'column' : 'row' }}
      >
        <InputNumber
          label="X"
          theme='column'
          value={selectedId ? posX : ''}
          placeholder=''
          disabled={disabled || !selectedId}
          step={0.01}
          // min={-1}
          // max={1}
          allowInputOverLimit={false}
          onChange={val => handlePositionChange('x', Number(val) || 0)}
        />
        <InputNumber
          label="Y"
          theme='column'
          value={selectedId ? posY : ''}
          placeholder=''
          disabled={disabled || !selectedId}
          step={0.01}
          // min={-1}
          // max={1}
          allowInputOverLimit={false}
          onChange={val => handlePositionChange('y', Number(val) || 0)}
        />
        <InputNumber
          label="缩放"
          theme='column'
          value={selectedId ? scale : ''}
          placeholder=''
          disabled={disabled || !selectedId}
          step={0.01}
          min={MIN_SCALE}
          max={MAX_SCALE}
          allowInputOverLimit={false}
          onChange={val => handlePositionChange('scale', Number(val) || 0)}
        />
      </div>

      <Stage
        ref={stageRef}
        width={bgSize.width}
        height={bgSize.height}
        style={{ borderRadius: 10, overflow: 'hidden' }}
        onClick={handleStageClick}
      >
        <Layer>
          {/* 背景层 */}
          {background ? (
            <Image
              image={bgImage}
              width={bgSize.width}
              height={bgSize.height}
              listening={false}
              onLoad={onBgLoad}
            />
          ) : (
            <Rect
              width={DEFAULT_RATIO.width}
              height={DEFAULT_RATIO.height}
              fill="#000000"
              listening={false}
            />
          )}

          {/* 图片层 */}
          {imageList.map(img => (
            <CustomImage
              key={img.key}
              item={img}
              disabled={disabled}
              bgSize={bgSize}
              imageRefs={imageRefs}
              transformerRef={transformerRef}
              onSelect={handleImageClick}
              onTransform={handleImageTransform}
            />
          ))}

          <Transformer
            ref={transformerRef}
            keepRatio={true}
            centeredScaling={true}
            resizeEnabled={!disabled}
            rotateEnabled={false}
            enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
            anchorCornerRadius={5}
            anchorFill="#ffffff"
            anchorStroke={PRIMARY_COLOR}
            borderStroke={PRIMARY_COLOR}
            borderDash={[10, 5]}
            boundBoxFunc={(oldBox, newBox) => {
              // 限制最小尺寸
              if (newBox.width < 10 || newBox.height < 10) return oldBox;
              return newBox;
            }}
          />
        </Layer>
      </Stage>
    </div>
  );
});

export default CustomKonva;
